import { useState, useEffect } from 'react';
import { Sparkle, X } from '@phosphor-icons/react';
import MigrationConcierge from './MigrationConcierge';
import { MIGRATION_CRUNCHY } from './crunchyConfigs';

/**
 * Floating "Ask Crunchy" button that opens a Crunchy chat in a drawer.
 * Config-driven (crunchyConfigs.js): defaults to the MigrationCrunch
 * concierge; pass cfg={SALES_CRUNCHY} for the SalesCrunchCRM salesperson.
 *
 * After 15 seconds on the page, a small "peek" bubble slides out from the
 * button to nudge undecided visitors into starting a conversation. The peek
 * dismisses once (per visitor, per variant) so we don't nag returning users.
 */
export default function MigrationConciergeFloat({ cfg = MIGRATION_CRUNCHY }) {
  const [open, setOpen] = useState(false);
  const [showPeek, setShowPeek] = useState(false);

  // Cmd+? opens the concierge
  useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === '?') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  // 15-second proactive greeting — fires ONCE per visitor via localStorage.
  // If they've already dismissed it (or opened the drawer manually), skip.
  useEffect(() => {
    if (localStorage.getItem(cfg.peekSeenKey) === '1') return;
    const t = setTimeout(() => {
      if (!open) setShowPeek(true);
    }, 15000);
    return () => clearTimeout(t);
  }, [open, cfg.peekSeenKey]);

  const dismissPeek = () => {
    setShowPeek(false);
    try { localStorage.setItem(cfg.peekSeenKey, '1'); } catch (_) { /* ignore */ }
  };

  const openFromPeek = () => {
    dismissPeek();
    setOpen(true);
  };

  return (
    <>
      {!open && (
        <>
          {/* Proactive peek bubble — slides up from the button after 15s */}
          {showPeek && (
            <div
              className={`fixed ${cfg.peekPos} right-5 z-[1000000] w-[280px] bg-white rounded-lg shadow-2xl border border-[#F39C12]/40 p-4 animate-in slide-in-from-bottom-4 fade-in duration-500`}
              data-testid={cfg.peekTid}
              role="dialog"
              aria-label="Crunchy proactive greeting"
            >
              <button
                onClick={dismissPeek}
                className="absolute top-2 right-2 text-slate-400 hover:text-slate-700 p-0.5"
                data-testid={`${cfg.peekTid}-close`}
                aria-label="Close"
              >
                <X size={14} weight="bold" />
              </button>
              <div className="flex items-start gap-2 mb-2">
                <div className="text-2xl leading-none">🍿</div>
                <div className="text-[13px] font-bold text-slate-900 leading-tight pr-3">
                  {cfg.peekTitle}
                </div>
              </div>
              <p className="text-[11px] text-slate-600 leading-relaxed mb-3">
                {cfg.peekBody}
              </p>
              <button
                onClick={openFromPeek}
                className="w-full bg-gradient-to-r from-[#F39C12] to-[#E08C00] hover:from-[#E08C00] hover:to-[#C77B00] text-black font-bold text-[12px] rounded px-3 py-2 transition-all"
                data-testid={`${cfg.peekTid}-open`}
              >
                Sure, let&apos;s chat →
              </button>
              {/* Speech-bubble tail pointing down toward the button */}
              <div className="absolute -bottom-2 right-8 w-4 h-4 bg-white border-r border-b border-[#F39C12]/40 transform rotate-45"></div>
            </div>
          )}
          <button
            onClick={() => { dismissPeek(); setOpen(true); }}
            data-testid={`${cfg.tid}-concierge-open`}
            className={`fixed ${cfg.btnPos} right-5 z-[1000000] bg-gradient-to-r from-[#F39C12] to-[#E08C00] hover:from-[#E08C00] hover:to-[#C77B00] text-black rounded-full shadow-lg hover:shadow-xl transition-all px-5 py-3 flex items-center gap-2 font-bold group`}
          >
            <Sparkle size={18} weight="fill" className="text-white group-hover:rotate-12 transition-transform" />
            <span>Ask Crunchy 🍿</span>
          </button>
        </>
      )}

      {open && (
        <div
          className={`fixed ${cfg.drawerPos} right-5 z-[1000000] w-[380px] max-w-[calc(100vw-2rem)] max-h-[calc(100vh-11rem)] flex flex-col`}
          data-testid={`${cfg.tid}-concierge-drawer`}
        >
          <button
            onClick={() => setOpen(false)}
            className="absolute top-3 right-3 z-10 text-white/80 hover:text-white p-1"
            data-testid={`${cfg.tid}-concierge-close`}
            title="Close"
          >
            <X size={18} weight="bold" />
          </button>
          <MigrationConcierge cfg={cfg} />
        </div>
      )}
    </>
  );
}
